/* v4 — Bright Candy
   Lean into the JobsSuperappPage DNA. Saturated tile colors, bigger sans
   headlines, more visual energy. Less serif, more confident. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink, FileCheck2, FileSearch, Mail, Mic, UserCheck, Video } from 'lucide-react';
import { useDashboardData, buildTaskRoute } from '../useDashboardData';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import { VariantSwitcher } from './VariantSwitcher';
import styles from './home-v4.module.css';

function greetingFor(d: Date) {
    const h = d.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function relativeAgo(ms: number) {
    const m = Math.max(1, Math.round(ms / 60_000));
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
}

function relativeFrom(iso: string) {
    return relativeAgo(Date.now() - new Date(iso).getTime());
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

function buildBriefing(userName: string, jobs: JobPosting[], tasks: TaskItem[], events: SyncedCalendarEvent[]) {
    const greeting = `${greetingFor(new Date())}, ${userName.split(' ')[0]}.`;
    const primary = jobs[0];
    const secondary = jobs[1];
    const high = tasks.filter((t) => t.priority === 'high');
    const next = events[0];
    const sourced = primary ? 31 + (hash(primary._id) % 32) : 0;
    const screened = Math.max(8, Math.round(sourced * 0.6));
    const passed = Math.max(high.length, Math.round(screened * 0.25));

    if (jobs.length === 0 && tasks.length === 0) {
        return { greeting, paragraph: 'Quiet morning. Nothing pending.' };
    }

    const lines: string[] = [];
    if (primary) {
        lines.push(
            `Sourced **${sourced} candidates** for **${primary.title}** overnight, screened ${screened}, ${passed} passed.${high.length > 0 ? ` **${high.length} need your judgment.**` : ''}`,
        );
    }
    if (secondary) {
        const drafted = 8 + (hash(secondary._id) % 9);
        lines.push(`Drafted outreach for ${drafted} warm referrals on **${secondary.title}**.`);
    }
    if (next) {
        const who = next.attendees?.[0]?.name?.split(' ')[0] || 'Someone';
        lines.push(`${who} confirmed at ${formatTime(next.startDateTime)}.`);
    }
    return { greeting, paragraph: lines.join(' ') };
}

function renderBold(text: string) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
    );
}

function renderActivity(text: string) {
    return text.split(/(\*[^*]+\*)/g).map((p, i) =>
        p.startsWith('*') && p.endsWith('*') ? <em key={i}>{p.slice(1, -1)}</em> : <span key={i}>{p}</span>,
    );
}

const ICON_BY = {
    sourcing: { Comp: FileSearch, cls: styles.iconSourcing },
    screening: { Comp: FileCheck2, cls: styles.iconScreening },
    nurture: { Comp: Mail, cls: styles.iconNurture },
    interview: { Comp: Mic, cls: styles.iconInterview },
    human: { Comp: UserCheck, cls: styles.iconHuman },
};

const AVATAR_COLORS = [styles.avatarA, styles.avatarB, styles.avatarC, styles.avatarD, styles.avatarE];

function initials(name: string) {
    const p = name.trim().split(/\s+/);
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function formatDue(due?: string) {
    if (!due) return null;
    const diff = new Date(due).getTime() - Date.now();
    const overdue = diff < 0;
    const abs = Math.abs(diff);
    if (abs < 3600_000) {
        const m = Math.max(1, Math.round(abs / 60_000));
        return { text: overdue ? `${m}m late` : `${m}m`, overdue };
    }
    if (abs < 24 * 3600_000) {
        const h = Math.round(abs / 3600_000);
        return { text: overdue ? `${h}h late` : `${h}h`, overdue };
    }
    const d = Math.round(abs / (24 * 3600_000));
    return { text: overdue ? `${d}d late` : `${d}d`, overdue };
}

export function HomeV4() {
    const { state, userName } = useDashboardData();
    const navigate = useNavigate();

    const briefing = useMemo(() => {
        if (state.phase !== 'ready') return null;
        return buildBriefing(userName, state.data.jobs, state.data.tasks, state.data.events);
    }, [state, userName]);

    if (state.phase === 'loading') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <VariantSwitcher current="v4" className={styles.variantBar} activeClass={styles.active} />
                    <div className={styles.skeleton} />
                </div>
            </main>
        );
    }
    if (state.phase === 'error') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.errorBox}>{state.message}</div>
                </div>
            </main>
        );
    }

    const { jobs, tasks, events, activity, lastActionAgoMs } = state.data;
    const high = tasks.filter((t) => t.priority === 'high');
    const primary = jobs[0];
    const secondary = jobs[1];
    const next = events[0];

    const chips: Array<{ label: string; href: string; primary?: boolean }> = [];
    if (high.length > 0) chips.push({ label: `${high.length} need your judgment`, href: '/v2/jobs', primary: true });
    if (next) chips.push({ label: `${next.attendees?.[0]?.name?.split(' ')[0] || 'Next'} at ${formatTime(next.startDateTime)}`, href: '/v2/calendar' });
    if (secondary) chips.push({ label: `Approve outreach`, href: `/v2/jobs/${secondary._id}/plan` });
    if (primary) chips.push({ label: `${primary.title} pipeline`, href: `/v2/jobs/${primary._id}/plan` });
    chips.push({ label: 'Lantern brain', href: '/brain' });

    return (
        <main className={styles.page}>
            <div className={styles.container}>
                <VariantSwitcher current="v4" className={styles.variantBar} activeClass={styles.active} />

                {/* HERO */}
                <section className={styles.hero}>
                    <div className={styles.heroAvatarWrap}>
                        <div className={styles.heroAvatar}>
                            <img src="/avatars/jacinta.svg" alt="" />
                        </div>
                    </div>
                    <div className={styles.heroBody}>
                        <div className={styles.heroIdent}>
                            <span className={styles.heroName}>Jacinta</span>
                            <span className={styles.heroStatus}>
                                <span className={styles.heroDot} aria-hidden />
                                last action {relativeAgo(lastActionAgoMs)}
                            </span>
                        </div>
                        <h1 className={styles.heroGreeting}>{briefing?.greeting}</h1>
                        <p className={styles.heroParagraph}>{briefing && renderBold(briefing.paragraph)}</p>
                        <div className={styles.chips}>
                            {chips.slice(0, 5).map((c, i) => (
                                <button
                                    key={c.href + i}
                                    className={`${styles.chip} ${c.primary ? styles.chipPrimary : ''}`}
                                    onClick={() => navigate(c.href)}
                                >
                                    {c.label}
                                    <ArrowRight size={12} strokeWidth={2.5} />
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ROLES — saturated funnel tiles */}
                <section className={styles.roles}>
                    <div className={styles.cardHead}>
                        <h2 className={styles.cardTitle}>Active roles</h2>
                        <span className={styles.cardSub}>{jobs.length} open · scroll to see more</span>
                    </div>
                    <div className={styles.rolesScroll}>
                        {jobs.slice(0, 5).map((job) => {
                            const seed = hash(job._id);
                            return (
                                <button key={job._id} className={styles.roleCard} onClick={() => navigate(`/v2/jobs/${job._id}/plan`)}>
                                    <div>
                                        <div className={styles.roleTitle}>{job.title}</div>
                                        <div className={styles.roleMeta}>{job.department || job.location || 'Workspace'}</div>
                                    </div>
                                    <div className={styles.funnelTiles}>
                                        <div className={`${styles.funnelTile} ${styles.tileSourced}`}>
                                            <span className={styles.funnelTileNum}>{35 + (seed % 23)}</span>
                                            <span className={styles.funnelTileLabel}>Sourced</span>
                                        </div>
                                        <div className={`${styles.funnelTile} ${styles.tileScreened}`}>
                                            <span className={styles.funnelTileNum}>{9 + (seed % 8)}</span>
                                            <span className={styles.funnelTileLabel}>Screened</span>
                                        </div>
                                        <div className={`${styles.funnelTile} ${styles.tileInterview}`}>
                                            <span className={styles.funnelTileNum}>{3 + (seed % 4)}</span>
                                            <span className={styles.funnelTileLabel}>Interview</span>
                                        </div>
                                        <div className={`${styles.funnelTile} ${styles.tileOffer}`}>
                                            <span className={styles.funnelTileNum}>{seed % 3}</span>
                                            <span className={styles.funnelTileLabel}>Offer</span>
                                        </div>
                                    </div>
                                    <div className={styles.roleFooter}>
                                        <span>Updated <strong>{relativeFrom(job.updatedAt)}</strong></span>
                                        <span>{job.status === 'open' ? 'Open' : job.status}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </section>

                <div className={styles.twoCol}>
                    {/* APPROVALS */}
                    <section className={styles.approvals}>
                        <div className={styles.cardHead}>
                            <h2 className={styles.cardTitle}>Pending approvals</h2>
                            <span className={styles.cardSub}>{tasks.length} waiting</span>
                        </div>
                        {tasks.slice(0, 5).map((task, i) => {
                            const due = formatDue(task.dueAt);
                            return (
                                <button key={task._id} className={styles.approvalRow} onClick={() => navigate(buildTaskRoute(task))}>
                                    <div className={`${styles.approvalAvatar} ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                                        {initials(task.candidateName)}
                                    </div>
                                    <div className={styles.approvalIdent}>
                                        <div className={styles.approvalName}>{task.candidateName}</div>
                                        <div className={styles.approvalSub}>{task.jobTitle}</div>
                                    </div>
                                    <div className={styles.approvalRight}>
                                        <span className={`${styles.kindPill} ${task.kind === 'cv_review' ? styles.kindCv : styles.kindInterview}`}>
                                            {task.kind === 'cv_review' ? 'CV' : 'INT'}
                                        </span>
                                        {due && <span className={`${styles.dueChip} ${due.overdue ? styles.dueChipOverdue : ''}`}>{due.text}</span>}
                                    </div>
                                </button>
                            );
                        })}
                        {tasks.length === 0 && <div className={styles.empty}>None pending.</div>}
                    </section>

                    {/* ACTIVITY */}
                    <section className={styles.activity}>
                        <div className={styles.cardHead}>
                            <h2 className={styles.cardTitle}>Recent activity</h2>
                            <span className={styles.cardSub}>{activity.length}</span>
                        </div>
                        <div className={styles.activityList}>
                            {activity.slice(0, 7).map((ev) => {
                                const ic = ICON_BY[ev.iconName];
                                const Comp = ic.Comp;
                                return (
                                    <div key={ev.id} className={styles.activityRow}>
                                        <div className={`${styles.activityIcon} ${ic.cls}`}>
                                            <Comp size={13} strokeWidth={2} />
                                        </div>
                                        <div className={styles.activityText}>{renderActivity(ev.text)}</div>
                                        <div className={styles.activityTime}>{relativeFrom(ev.at)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {/* CALENDAR */}
                <section className={styles.calendar}>
                    <div className={styles.cardHead}>
                        <h2 className={styles.cardTitle}>Today</h2>
                        <span className={styles.cardSub}>{events.length === 0 ? 'no events' : `${events.length} upcoming`}</span>
                    </div>
                    {events.slice(0, 4).map((ev) => {
                        const who =
                            ev.attendees?.[0]?.name || ev.attendees?.[0]?.email?.split('@')[0] || ev.organizer?.name || '';
                        return (
                            <div key={ev._id} className={styles.calRow}>
                                <span className={styles.calTime}>{formatTime(ev.startDateTime)}</span>
                                <div className={styles.calBody}>
                                    <div className={styles.calTitle}>{ev.title}</div>
                                    {who && <div className={styles.calSub}>{who}</div>}
                                </div>
                                {ev.meetingUrl ? (
                                    <a className={styles.calCta} href={ev.meetingUrl} target="_blank" rel="noopener noreferrer">
                                        <Video size={12} strokeWidth={2.5} /> Join
                                    </a>
                                ) : (
                                    <button className={`${styles.calCta} ${styles.calCtaGhost}`} onClick={() => navigate('/v2/calendar')}>
                                        <ExternalLink size={12} strokeWidth={2.5} /> Open
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {events.length === 0 && <div className={styles.empty}>Nothing scheduled.</div>}
                </section>
            </div>
        </main>
    );
}
